import type { DollarsString } from './dollars';
import type { OrderAction, OrderStatus } from './order';

export type WsSubscriberCallback = (msg: WsResponse) => void;

// Returns true if the subscription was successfully unsubscribed from, false otherwise.
export type WsUnsubscribeCallback = () => boolean;

export const EVENT_CONTRACT_WS_CHANNELS = [
    'trades',
    'orderbook',
    'ticker',
    'user_trades',
    'user_orders',
    'user_balance',
    'cdc_qe_user_positions',
    'market_lifecycle_v2',
    'market_metadata',
] as const;

export const MARGIN_WS_CHANNELS = ['ticker', 'orderbook_delta', 'fill', 'user_orders', 'trade'] as const;

export type EventContractWsChannel = (typeof EVENT_CONTRACT_WS_CHANNELS)[number];
export type MarginWsChannel = (typeof MARGIN_WS_CHANNELS)[number];
export type WsChannel = EventContractWsChannel | MarginWsChannel;

export interface WsSubscribeParams<TChannel extends WsChannel = WsChannel> {
    channels: Array<TChannel>;
    market_id?: string;
    market_ids?: Array<string>;
    market_tickers?: Array<string>;
    send_initial_snapshot?: boolean;
    subaccount?: number;
}

export interface WsUnsubscribeParams {
    sids: number[];
}

export type WsUpdateSubscriptionAction = 'add_markets' | 'delete_markets';

export interface WsUpdateSubscriptionParams {
    sid?: number;
    sids?: number[];
    action: WsUpdateSubscriptionAction;
    market_ticker?: string;
    market_tickers?: string[];
    market_id?: string;
    market_ids?: string[];
    send_initial_snapshot?: boolean;
}

export interface WsSubscription {
    params: WsSubscribeParams;
    subscribers: Set<WsSubscriberCallback>;
}

export interface WsCommand {
    id: number;
    cmd: 'subscribe' | 'unsubscribe' | 'update_subscription';
    params: WsSubscribeParams | WsUnsubscribeParams | WsUpdateSubscriptionParams;
}

export interface WsMessage {
    id?: number;
    sid?: number;
    seq?: number;
    type:
        | 'subscribed'
        | 'unsubscribed'
        | 'error'
        | 'trades'
        | 'orderbook_snapshot'
        | 'ticker'
        | 'user_balance'
        | 'user_order'
        | 'user_trade'
        | 'user_position'
        | 'market_lifecycle_v2'
        | 'market_metadata'
        | 'orderbook_delta'
        | 'trade';

    msg: WsMessagePayload;
}

export type WsResponse = WsMessage | WsMessage[];

export type SubscribedDTO = {
    channel: string;
    sid: number;
};

export type WsErrorDTO = {
    code: number;
    msg: string;
};

export type SnapshotDTO = {
    market_id: string;
    market_ticker?: string;
    yes?: Array<[number, number]>;
    no?: Array<[number, number]>;
    yes_dollars_fp?: Array<[DollarsString, string]>;
    no_dollars_fp?: Array<[DollarsString, string]>;
};

export type TickerDTO = {
    market_id: string;
    market_ticker: string;
    price: number;
    price_dollars: DollarsString;
    yes_bid: number;
    yes_bid_dollars: DollarsString;
    yes_ask: number;
    yes_ask_dollars: DollarsString;
    volume: number;
    volume_fp?: string;
    previous_volume: number;
    dollar_volume: number; // is (volume / 2)
    open_interest: number;
    open_interest_fp?: string;
    dollar_open_interest: number; // is (open_interest / 2)
    ts: string;
};

export type UserOrderDTO = {
    order_id: string;
    user_id: string;
    market_id: string;
    market_ticker: string;
    is_yes: boolean;
    price: number;
    price_dollars: DollarsString;
    create_ts: string;
    taker_fill_cost: number;
    taker_fill_cost_dollars?: DollarsString;
    maker_fill_cost: number;
    taker_fees: number;
    taker_fees_dollars?: DollarsString;
    maker_fees: number;
    maker_fees_dollars?: DollarsString;
    remaining_count: number;
    remaining_count_fp?: string;
    initial_count: number;
    initial_count_fp?: string;
    fill_count: number;
    fill_count_fp?: string;
    expiration_ts: string | null;
    // Exists only if provided when order is created
    order_action?: OrderAction;
    status: OrderStatus;
    // Exists only if the order is updated after creation
    updated_ts?: string;
    last_update_op: string;
    subaccount?: number;
};

export type UserTradeDTO = {
    type: 'user_trade';
    id: string;
    order_id: string;
    market_id: string;
    is_taker: boolean;
    is_yes: boolean;
    ts: number;
    price: number;
    price_dollars: DollarsString;
    count: number;
    count_fp?: string;
    subaccount?: number;
};

export type PositionDTO = {
    user_id: string;
    market_id: string;
    market_ticker: string;
    event_ticker: string;
    yes_trade_count: number;
    yes_trade_count_fp?: string;
    no_trade_count: number;
    no_trade_count_fp?: string;
    position_fp: string;
    position_cost_dollars: DollarsString;
    total_cost_dollars: DollarsString;
    buying_cost_dollars: DollarsString;
    realized_pnl_dollars: DollarsString;
    fees_paid_dollars: DollarsString;
    yes_buying_fee_cost_dollars?: DollarsString;
    no_buying_fee_cost_dollars?: DollarsString;
    position_fee_cost_dollars?: DollarsString;
    volume_fp: string;
    final_position_fp: string;
    final_position_cost_dollars: DollarsString;
    final_position_fee_cost_dollars?: DollarsString;
    is_settled: boolean;
    last_position_fp: string;
    subaccount?: number;
};

export type LifecycleDTO = {
    market_ticker: string; // Unique identifier for markets. This is what you use to differentiate updates for different markets.
    event_type?: string;
    open_ts?: number; // Unix timestamp for when the market opened (in seconds).
    close_ts?: number; // Unix timestamp for when the market is scheduled to close (in seconds). Will be updated in case of early determination markets.
    determination_ts?: number; // Optional: This key will not exist before the market is determined. Unix timestamp for when the market is determined (in seconds)
    settled_ts?: number; // Optional: This key will not exist before the market is settled. Unix timestamp for when the market is settled (in seconds).
    result?: string; // Optional: This key will not exist before the market is determined. Result of the market.
    is_deactivated?: boolean; // Boolean flag to indicate if trading is paused on an open market. This should only be interpreted for an open market.
    settlement_value?: string; // Optional: FixedPointDollars (e.g. "12.3400"). Only set on `market_lifecycle_determined` events from the public external WS; the internal WS doesn't emit it.
};

// One of the two will be provided
export type MarketMetadataDTO = {
    event_ticker?: string;
    market_ticker?: string;
};

type WsMessagePayload =
    | SnapshotDTO
    | TickerDTO
    | UserOrderDTO
    | UserTradeDTO
    | SubscribedDTO
    | WsErrorDTO
    | PositionDTO
    | LifecycleDTO
    | MarketMetadataDTO;
